/* ==========================================================================
   XUYEL V12
   Premium Interaction Layer for Shopify Dawn
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     GLOBAL
     ------------------------------------------------------------------------ */

  window.XUYEL = window.XUYEL || {};

  const X = window.XUYEL;

  X.version = '12.0.0';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, {
        once: true
      });
    } else {
      callback();
    }
  };

  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;


  /* ------------------------------------------------------------------------
     UTILS
     ------------------------------------------------------------------------ */

  const qs = (selector, scope = document) =>
    scope.querySelector(selector);

  const qsa = (selector, scope = document) =>
    Array.from(scope.querySelectorAll(selector));

  const debounce = (callback, delay = 200) => {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);

      timeout = window.setTimeout(() => {
        callback(...args);
      }, delay);
    };
  };

  const announce = (message) => {
    const region = document.getElementById('XuyelLiveRegion');

    if (!region) return;

    region.textContent = '';

    window.setTimeout(() => {
      region.textContent = message;
    }, 30);
  };


  /* ------------------------------------------------------------------------
     DEVICE / PLATFORM
     ------------------------------------------------------------------------ */

  X.device = {
    isMobile:
      () =>
        window.matchMedia('(max-width: 749px)').matches,

    isDesktop:
      () =>
        window.matchMedia('(min-width: 750px)').matches,

    isTouch:
      () =>
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0
  };


  /* ------------------------------------------------------------------------
     HEADER SCROLL
     ------------------------------------------------------------------------ */

  function initHeader() {
    const header = qs('.header-wrapper');

    if (!header) return;

    header.classList.add('xuyel-header-fixed');

    let ticking = false;

    const update = () => {
      const scrolled =
        window.scrollY > 12;

      document.documentElement.classList.toggle(
        'xuyel-scrolled',
        scrolled
      );

      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;

      ticking = true;

      window.requestAnimationFrame(update);
    };

    update();

    window.addEventListener(
      'scroll',
      onScroll,
      {
        passive: true
      }
    );
  }


  /* ------------------------------------------------------------------------
     SCROLL REVEAL
     ------------------------------------------------------------------------ */

  function initReveal() {
    const elements =
      qsa('.xuyel-reveal');

    if (!elements.length) return;

    if (
      prefersReducedMotion() ||
      !('IntersectionObserver' in window)
    ) {
      elements.forEach((element) => {
        element.classList.add('is-visible');
      });

      return;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add(
              'is-visible'
            );

            observer.unobserve(
              entry.target
            );
          });
        },
        {
          rootMargin:
            '0px 0px -8% 0px',

          threshold: 0.05
        }
      );

    elements.forEach((element) => {
      observer.observe(element);
    });
  }


  /* ------------------------------------------------------------------------
     SMOOTH INTERNAL LINKS
     ------------------------------------------------------------------------ */

  function initSmoothLinks() {
    document.addEventListener(
      'click',
      (event) => {
        const link =
          event.target.closest(
            'a[href^="#"]'
          );

        if (!link) return;

        const href =
          link.getAttribute('href');

        if (
          !href ||
          href === '#' ||
          href.length < 2
        ) {
          return;
        }

        const target =
          document.querySelector(href);

        if (!target) return;

        event.preventDefault();

        const headerHeight =
          X.device.isMobile()
            ? 64
            : 72;

        const top =
          target.getBoundingClientRect().top +
          window.scrollY -
          headerHeight -
          12;

        window.scrollTo({
          top,
          behavior:
            prefersReducedMotion()
              ? 'auto'
              : 'smooth'
        });

        history.replaceState(
          null,
          '',
          href
        );
      }
    );
  }


  /* ------------------------------------------------------------------------
     IMAGE LAZY EFFECT
     ------------------------------------------------------------------------ */

  function initImages() {
    const images =
      qsa('img');

    if (!images.length) return;

    images.forEach((image) => {
      image.addEventListener(
        'load',
        () => {
          image.classList.add(
            'xuyel-image-loaded'
          );
        },
        {
          once: true
        }
      );
    });
  }


  /* ------------------------------------------------------------------------
     MOBILE NAV
     ------------------------------------------------------------------------ */

  function initMobileNavigation() {
    const nav =
      qs('.xuyel-mobile-nav');

    if (!nav) return;

    const items =
      qsa(
        '.xuyel-mobile-nav__item',
        nav
      );

    if (!items.length) return;

    const currentPath =
      window.location.pathname;

    items.forEach((item) => {
      const href =
        item.getAttribute('href');

      if (!href) return;

      try {
        const url =
          new URL(
            href,
            window.location.origin
          );

        const path =
          url.pathname;

        if (
          path === currentPath ||
          (
            path !== '/' &&
            currentPath.startsWith(path)
          )
        ) {
          item.classList.add(
            'is-active'
          );

          item.setAttribute(
            'aria-current',
            'page'
          );
        }
      } catch (error) {
        /* Ignore invalid navigation URL */
      }
    });

    items.forEach((item) => {
      item.addEventListener(
        'click',
        () => {
          items.forEach((other) => {
            other.classList.remove(
              'is-active'
            );

            other.removeAttribute(
              'aria-current'
            );
          });

          item.classList.add(
            'is-active'
          );

          item.setAttribute(
            'aria-current',
            'page'
          );
        }
      );
    });
  }


  /* ------------------------------------------------------------------------
     CART COUNT
     ------------------------------------------------------------------------ */

  function getCartCount() {
    const countElements =
      qsa(
        '[data-cart-count], .cart-count-bubble span'
      );

    return countElements;
  }

  function updateCartCount(count) {
    const elements =
      getCartCount();

    elements.forEach((element) => {
      element.textContent =
        count > 99
          ? '99+'
          : String(count);

      element.classList.toggle(
        'is-empty',
        count <= 0
      );
    });

    const navBadge =
      qs(
        '.xuyel-mobile-nav__badge'
      );

    if (navBadge) {
      navBadge.textContent =
        count > 99
          ? '99+'
          : String(count);

      navBadge.hidden =
        count <= 0;
    }
  }

  async function refreshCartCount() {
    try {
      const response =
        await fetch(
          '/cart.js',
          {
            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (!response.ok) return;

      const cart =
        await response.json();

      updateCartCount(
        Number(cart.item_count || 0)
      );
    } catch (error) {
      /* Cart endpoint unavailable */
    }
  }


  /* ------------------------------------------------------------------------
     CART EVENTS
     ------------------------------------------------------------------------ */

  function initCartEvents() {
    document.addEventListener(
      'cart:updated',
      (event) => {
        const count =
          event.detail?.cart?.item_count;

        if (
          typeof count ===
          'number'
        ) {
          updateCartCount(count);
        } else {
          refreshCartCount();
        }
      }
    );

    document.addEventListener(
      'xuyel:cart-updated',
      (event) => {
        const count =
          event.detail?.itemCount;

        if (
          typeof count ===
          'number'
        ) {
          updateCartCount(count);
        }
      }
    );

    refreshCartCount();
  }


  /* ------------------------------------------------------------------------
     ADD TO CART FEEDBACK
     ------------------------------------------------------------------------ */

  function showToast(message) {
    let toast =
      qs('.xuyel-toast');

    if (!toast) {
      toast =
        document.createElement(
          'div'
        );

      toast.className =
        'xuyel-toast';

      toast.setAttribute(
        'role',
        'status'
      );

      document.body.appendChild(
        toast
      );
    }

    toast.textContent =
      message;

    toast.classList.add(
      'is-visible'
    );

    window.clearTimeout(
      toast._timeout
    );

    toast._timeout =
      window.setTimeout(() => {
        toast.classList.remove(
          'is-visible'
        );
      }, 2600);
  }

  X.toast = showToast;


  /* ------------------------------------------------------------------------
     ADD TO CART BUTTON LISTENER
     ------------------------------------------------------------------------ */

  function initAddToCartFeedback() {
    document.addEventListener(
      'submit',
      (event) => {
        const form =
          event.target;

        if (
          !form.matches(
            'form[action*="/cart/add"]'
          )
        ) {
          return;
        }

        const button =
          qs(
            '[type="submit"]',
            form
          );

        if (!button) return;

        const originalText =
          button.textContent;

        button.classList.add(
          'xuyel-loading'
        );

        button.setAttribute(
          'aria-busy',
          'true'
        );

        window.setTimeout(() => {
          button.classList.remove(
            'xuyel-loading'
          );

          button.removeAttribute(
            'aria-busy'
          );

          if (
            document.visibilityState ===
            'visible'
          ) {
            showToast(
              'Added to cart'
            );

            announce(
              'Product added to cart'
            );
          }

          button.textContent =
            originalText;
        }, 850);
      }
    );
  }


  /* ------------------------------------------------------------------------
     PRODUCT CARD HOVER
     ------------------------------------------------------------------------ */

  function initProductCards() {
    const cards =
      qsa('.card-wrapper');

    if (!cards.length) return;

    cards.forEach((card) => {
      card.addEventListener(
        'mouseenter',
        () => {
          card.classList.add(
            'xuyel-card-hover'
          );
        }
      );

      card.addEventListener(
        'mouseleave',
        () => {
          card.classList.remove(
            'xuyel-card-hover'
          );
        }
      );
    });
  }


  /* ------------------------------------------------------------------------
     CATEGORY CHIPS
     ------------------------------------------------------------------------ */

  function initCategoryChips() {
    const lists =
      qsa(
        '.xuyel-category-list'
      );

    lists.forEach((list) => {
      const chips =
        qsa(
          '.xuyel-category-chip',
          list
        );

      chips.forEach((chip) => {
        chip.addEventListener(
          'click',
          () => {
            chips.forEach((item) => {
              item.classList.remove(
                'is-active'
              );
            });

            chip.classList.add(
              'is-active'
            );
          }
        );
      });
    });
  }


  /* ------------------------------------------------------------------------
     ESCAPE KEY
     ------------------------------------------------------------------------ */

  function initEscapeHandler() {
    document.addEventListener(
      'keydown',
      (event) => {
        if (
          event.key !== 'Escape'
        ) {
          return;
        }

        document
          .querySelectorAll(
            '.xuyel-toast.is-visible'
          )
          .forEach((element) => {
            element.classList.remove(
              'is-visible'
            );
          });
      }
    );
  }


  /* ------------------------------------------------------------------------
     VIEWPORT STATE
     ------------------------------------------------------------------------ */

  function initViewportState() {
    const update =
      debounce(() => {
        document.documentElement
          .classList.toggle(
            'xuyel-mobile',
            X.device.isMobile()
          );

        document.documentElement
          .classList.toggle(
            'xuyel-desktop',
            X.device.isDesktop()
          );
      }, 80);

    update();

    window.addEventListener(
      'resize',
      update,
      {
        passive: true
      }
    );
  }


  /* ------------------------------------------------------------------------
     ONLINE / OFFLINE
     ------------------------------------------------------------------------ */

  function initNetworkState() {
    const update = () => {
      document.documentElement
        .classList.toggle(
          'xuyel-offline',
          !navigator.onLine
        );
    };

    update();

    window.addEventListener(
      'online',
      update
    );

    window.addEventListener(
      'offline',
      update
    );
  }


  /* ------------------------------------------------------------------------
     DESIGN MODE
     ------------------------------------------------------------------------ */

  function initDesignMode() {
    if (
      window.Shopify &&
      Shopify.designMode
    ) {
      document.documentElement
        .classList.add(
          'shopify-design-mode'
        );
    }
  }


  /* ------------------------------------------------------------------------
     PUBLIC API
     ------------------------------------------------------------------------ */

  X.utils = {
    qs,
    qsa,
    debounce,
    announce
  };

  X.cart = {
    refresh:
      refreshCartCount,

    updateCount:
      updateCartCount
  };


  /* ------------------------------------------------------------------------
     INITIALIZATION
     ------------------------------------------------------------------------ */

  ready(() => {

    document.documentElement
      .classList.add(
        'xuyel-ready'
      );

    initDesignMode();

    initHeader();

    initReveal();

    initSmoothLinks();

    initImages();

    initMobileNavigation();

    initCartEvents();

    initAddToCartFeedback();

    initProductCards();

    initCategoryChips();

    initEscapeHandler();

    initViewportState();

    initNetworkState();

  });


})();